import Link from "common/Typography/Link";
import React, {
  ChangeEvent,
  KeyboardEvent,
  useContext,
  useEffect,
  useState,
} from "react";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context";
import Text from "common/Typography/Text";
import styled, { css } from "styled-components";
import {
  LazyQueryStatus,
  NewPaymentPriceResponse,
  LanguageNameSpaces,
} from "@shared/types";
import { usePromoPrices } from "features/settings/Subscription/usePromoPrices.ts";
import Flex from "common/Boxes/Flex";
import Button from "common/Buttons/Button";
import { typography } from "@shared/common/Themes/typography";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import { PROMO_CODE_QUERY_PARAM } from "features/settings/common/constants.ts";
import Input from "common/Inputs/Input";
import Spin from "antd/es/spin";
import LoadingOutlined from "@ant-design/icons/LoadingOutlined";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface PromoCodeProps {
  onApplyButtonClick: () => void;
  onRemovePromoClick: () => void;
  getNewPaymentPriceStatus?: LazyQueryStatus<NewPaymentPriceResponse>;
}

const PromoCode = ({
  onApplyButtonClick,
  onRemovePromoClick,
  getNewPaymentPriceStatus,
}: PromoCodeProps) => {
  const { isBottomSummary, setPromoCode, promoCode, queryPromoCode } =
    useContext(BuySubscriptionContext)!;
  const {
    getPromoCodeDiscountMessage,
    getResultMessageColor,
    promoCodeMessage,
    isPromoCodeApplied,
  } = usePromoPrices();
  const [showPromoCodeInput, setShowPromoCodeInput] = useState(
    !!queryPromoCode?.length || isPromoCodeApplied
  );
  const { appendQuery } = useHistoryWithQuery();
  const { isError, isFetching } = getNewPaymentPriceStatus || {};
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);

  useEffect(() => {
    if (isPromoCodeApplied && promoCode && !queryPromoCode) {
      appendQuery({ [PROMO_CODE_QUERY_PARAM]: promoCode });
    }
  }, [isPromoCodeApplied]);

  const showMessage =
    (isError || isPromoCodeApplied) && promoCodeMessage !== undefined;
  const message = promoCodeMessage?.length
    ? promoCodeMessage
    : getPromoCodeDiscountMessage();

  const handleRemovePromo = () => {
    setShowPromoCodeInput(false);
    onRemovePromoClick();
    appendQuery(undefined, [PROMO_CODE_QUERY_PARAM]);
  };

  const getInputMessage = () => {
    if (!showMessage && !isPromoCodeApplied) return;
    return (
      <Flex style={{ height: 20 }}>
        {showMessage && (
          <ResultMessage style={{ color: getResultMessageColor() }}>
            {message}
          </ResultMessage>
        )}
        {isPromoCodeApplied && (
          <Button
            type="link"
            onClick={handleRemovePromo}
            style={{
              height: "100%",
              fontSize: typography.fontSizes.S,
              paddingRight: 0,
            }}
          >
            {t("promo_code.remove")}
          </Button>
        )}
      </Flex>
    );
  };

  return (
    <>
      {!showPromoCodeInput && (
        <PromoCodeButton
          onClick={() => setShowPromoCodeInput(!showPromoCodeInput)}
          $isBottomSummary={isBottomSummary}
        >
          {showPromoCodeInput ? "-" : "+"} {t("promo_code.add_promo_code")}
        </PromoCodeButton>
      )}
      {showPromoCodeInput && (
        <div style={{ width: "100%", maxWidth: 450, margin: "16px 0 12px 0" }}>
          {getInputMessage()}
          <Input
            autoFocus
            value={promoCode}
            placeholder={t("promo_code.promo_code_placeholder")}
            defaultValue={queryPromoCode}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setPromoCode(e.currentTarget.value)
            }
            onPressEnter={(e: KeyboardEvent<HTMLInputElement>) => {
              e.preventDefault();
              onApplyButtonClick();
            }}
            disabled={isPromoCodeApplied}
            suffix={
              <>
                <Spin
                  spinning={isFetching && !showMessage}
                  indicator={<LoadingOutlined spin />}
                  size="small"
                />
                {isPromoCodeApplied ? (
                  <SuccessMessage>{t("promo_code.applied")}</SuccessMessage>
                ) : (
                  <Link onClick={onApplyButtonClick}>
                    {t("promo_code.apply")}
                  </Link>
                )}
              </>
            }
          />
        </div>
      )}
    </>
  );
};

export default PromoCode;

const ResultMessage = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  margin-bottom: 2px;
`;
const SuccessMessage = styled(Text)`
  color: ${(p) => p.theme.colors.green};
`;
const PromoCodeButton = styled(Text)<{ $isBottomSummary?: boolean }>`
  color: ${(p) => p.theme.colors.primaryBlue};
  cursor: pointer;
  padding: 6px 12px;
  width: fit-content;
  ${(p) =>
    p.$isBottomSummary
      ? `padding-left: 0;`
      : `
    &:hover {
      background: ${p.theme.colors.lightBlue} !important;
      border-radius: 8px;
    }
  `};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* The label lines up with the checkboxes above; the hover tint still
         bleeds 8px around it, and it sits 16px from both neighbours. */
      && {
        margin: 4px 0 -4px ${p.$isBottomSummary ? 0 : -8}px;
        padding: 4px ${p.$isBottomSummary ? 0 : 8}px;
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      }
    `}
`;
